import { bookServiceApi } from '@/services/api';
import type { Book } from '@/features/library/api/books';
import { purchaseBook as purchaseBookApi, type PurchaseBookResponse } from '@/features/library/api/books';

export type LibraryStatus = 'saved' | 'in-progress' | 'completed';

export interface LibraryItem {
  id: number;
  book: Book;
  status: LibraryStatus;
  // progress in percentage (0-100) or pages; keep generic number
  progress: number;
  // server-provided saved flag
  is_saved?: boolean;
  added_at?: string;
  updated_at?: string;
  // optional multi-status support if backend provides multiple states
  statuses?: LibraryStatus[];
  // true only if backend explicitly marked as saved (not inferred/default)
  explicitSaved?: boolean;
  // optional metadata to display current chapter/section if the backend provides it
  chapter_title?: string;
  section_title?: string;
  chapter_index?: number;
}

export interface LibraryPagination {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface LibraryPaginatedResponse {
  items: LibraryItem[];
  count?: number;
  pagination?: LibraryPagination;
}

export interface LibraryResponse {
  items: LibraryItem[];
  count?: number;
}


// Alias detectors (no defaulting)
const toStr = (x: unknown) => (x == null ? '' : String(x)).toLowerCase();
const arrayOf = (s: unknown) => (Array.isArray(s) ? s : [s]);
function isSavedAlias(s: unknown): boolean {
  const vals = arrayOf(s).map(toStr);
  return vals.some((v) => ['saved', 'bookmark', 'bookmarked', 'favorite', 'favorited', 'ذخیره شده'].includes(v));
}
function isCompletedAlias(s: unknown): boolean {
  const vals = arrayOf(s).map(toStr);
  return vals.some((v) => ['completed', 'complete', 'finished', 'done', 'خوانده شده'].includes(v));
}
function isInProgressAlias(s: unknown): boolean {
  const vals = arrayOf(s).map(toStr);
  return vals.some((v) => ['in-progress', 'in_progress', 'progress', 'reading', 'ongoing', 'در حال خواندن'].includes(v));
}

const withAddedStatus = (it: LibraryItem, s: LibraryStatus, explicitSaved = false): LibraryItem => {
  const statuses = Array.from(new Set([...(it.statuses || []), s])) as LibraryStatus[];
  return { ...it, status: s, statuses, ...(explicitSaved ? { explicitSaved: true } : {}) };
};

// Fallback normalizer to handle multiple shapes
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeLibraryResponse(data: any): LibraryResponse {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapArrayToItems = (arr: any[]): LibraryItem[] => {
    return (arr || []).map((raw, idx) => {
      // Case A: already in desired shape
      if (raw && typeof raw === 'object' && raw.book) {
        const rawStatus = raw.status ?? raw.state ?? raw.status_fa;
        const statuses: LibraryStatus[] = [];
        if (isCompletedAlias(rawStatus)) statuses.push('completed');
        if (isInProgressAlias(rawStatus)) statuses.push('in-progress');
        if (isSavedAlias(rawStatus)) statuses.push('saved');
        let primary: LibraryStatus | undefined = (['completed', 'in-progress', 'saved'] as LibraryStatus[])
          .find((s) => statuses.includes(s));
        // If no status detected, infer from progress
        if (!primary) {
          const progressVal = typeof raw.progress === 'number' ? raw.progress : 0;
          if (progressVal >= 100) {
            primary = 'completed';
          } else if (progressVal > 0) {
            primary = 'in-progress';
          } else {
            primary = raw?.is_saved === true ? 'saved' : 'saved';
          }
        }
        const chapterTitle: string | undefined =
          raw.chapter_title ?? raw.current_chapter_title ?? raw.last_read_chapter_title ?? raw.chapterName ?? raw.section_title ?? raw.current_section_title ?? raw.last_section_title ?? raw?.chapter?.title;
        const sectionTitle: string | undefined = raw.section_title ?? raw.current_section_title ?? raw.last_section_title ?? raw?.section?.title;
        const chapterIndexRaw = raw.chapter_index ?? raw.current_chapter_index ?? raw.last_read_chapter_index ?? raw.chapter_no ?? raw.chapterNumber;
        return {
          id: raw.id ?? idx,
          book: raw.book as Book,
          status: primary,
          progress: typeof raw.progress === 'number' ? raw.progress : 0,
          added_at: raw.added_at,
          updated_at: raw.updated_at,
          statuses,
          explicitSaved: raw?.is_saved === true ? true : isSavedAlias(rawStatus),
          chapter_title: typeof chapterTitle === 'string' ? chapterTitle : undefined,
          section_title: typeof sectionTitle === 'string' ? sectionTitle : undefined,
          chapter_index: typeof chapterIndexRaw === 'number' ? chapterIndexRaw : (Number.isFinite(Number(chapterIndexRaw)) ? Number(chapterIndexRaw) : undefined),
        };
      }
      // Case B: flattened book with optional status/progress (book-service library response)
      const rawStatus = raw?.status ?? raw?.state ?? raw?.status_fa;
      const statuses: LibraryStatus[] = [];
      if (isCompletedAlias(rawStatus)) statuses.push('completed');
      if (isInProgressAlias(rawStatus)) statuses.push('in-progress');
      if (isSavedAlias(rawStatus)) statuses.push('saved');
      let status: LibraryStatus = (['completed', 'in-progress', 'saved'] as LibraryStatus[])
        .find((s) => statuses.includes(s)) as LibraryStatus;
      // If server didn't send a status, infer from progress
      if (!status) {
        const progressVal = typeof raw?.progress === 'number' ? raw.progress : 0;
        if (progressVal >= 100) {
          status = 'completed';
        } else if (progressVal > 0) {
          status = 'in-progress';
        } else {
          status = raw?.is_saved === true ? 'saved' : 'saved';
        }
      }
      const progressVal = typeof raw?.progress === 'number' ? raw.progress : Number(raw?.progress ?? 0);
      const chapterTitle: string | undefined =
        raw?.chapter_title ?? raw?.current_chapter_title ?? raw?.last_read_chapter_title ?? raw?.chapterName ?? raw?.section_title ?? raw?.current_section_title ?? raw?.last_section_title ?? raw?.chapter?.title;
      const sectionTitle: string | undefined = raw?.section_title ?? raw?.current_section_title ?? raw?.last_section_title ?? raw?.section?.title;
      const chapterIndexRaw = raw?.chapter_index ?? raw?.current_chapter_index ?? raw?.last_read_chapter_index ?? raw?.chapter_no ?? raw?.chapterNumber;
      // Build a minimal Book object from flattened fields
      const book: Book = {
        book_id: Number(raw?.book_id ?? raw?.id ?? idx),
        title: String(raw?.title ?? ''),
        author: String(raw?.author ?? ''),
        cover_image_url: raw?.cover_image_url ?? null,
        description: String(raw?.description ?? ''),
        price: String(raw?.price ?? ''),
        topic: String(raw?.topic ?? ''),
        created_at: String(raw?.created_at ?? ''),
        average_rating: typeof raw?.average_rating === 'number' ? raw.average_rating : undefined,
        rating: typeof raw?.rating === 'number' ? raw.rating : undefined,
        total_ratings: typeof raw?.total_ratings === 'number' ? raw.total_ratings : undefined,
      };
      return {
        id: Number(raw?.id ?? raw?.book_id ?? idx),
        book,
        status,
        progress: isFinite(progressVal) ? progressVal : 0,
        is_saved: raw?.is_saved === true,
        added_at: raw?.added_at,
        updated_at: raw?.updated_at,
        statuses,
        // prefer server flag for saved state
        explicitSaved: raw?.is_saved === true ? true : isSavedAlias(rawStatus),
        chapter_title: typeof chapterTitle === 'string' ? chapterTitle : undefined,
        section_title: typeof sectionTitle === 'string' ? sectionTitle : undefined,
        chapter_index: typeof chapterIndexRaw === 'number' ? chapterIndexRaw : (Number.isFinite(Number(chapterIndexRaw)) ? Number(chapterIndexRaw) : undefined),
      };
    });
  };

  if (Array.isArray(data)) {
    return { items: mapArrayToItems(data), count: data.length };
  }
  // Common envelopes
  if (data && Array.isArray(data.items)) {
    const items = mapArrayToItems(data.items);
    return { items, count: data.count ?? items.length };
  }
  // Other common envelope keys
  const candidates = data?.books || data?.library || data?.results || data?.data;
  if (Array.isArray(candidates)) {
    const items = mapArrayToItems(candidates);
    return { items, count: data?.count ?? items.length };
  }
  // book-service library response: { books: [...], total_books: number }
  if (data && Array.isArray(data?.books)) {
    const items = mapArrayToItems(data.books);
    return { items, count: typeof data.total_books === 'number' ? data.total_books : items.length };
  }
  // Grouped buckets { saved: [], completed: [], in_progress: [] }
  const buckets = data?.library || data;
  if (buckets && (Array.isArray(buckets.saved) || Array.isArray(buckets.completed) || Array.isArray(buckets.in_progress) || Array.isArray(buckets.inProgress))) {
    const savedArr = mapArrayToItems(buckets.saved || []).map((it) => withAddedStatus(it, 'saved', true));
    const completedArr = mapArrayToItems(buckets.completed || []).map((it) => withAddedStatus(it, 'completed'));
    const inProgArr = mapArrayToItems(buckets.in_progress || buckets.inProgress || []).map((it) => withAddedStatus(it, 'in-progress'));
    const items = [...savedArr, ...completedArr, ...inProgArr];
    return { items, count: items.length };
  }
  // Unknown shape -> empty list
  return { items: [], count: 0 };
}

export interface GetUserLibraryParams {
  page?: number;
  limit?: number;
}

export async function getUserLibrary(params: GetUserLibraryParams = {}): Promise<LibraryPaginatedResponse> {
  const { page = 1, limit = 10 } = params;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res = await bookServiceApi.get<any>(`/library/?page=${page}&limit=${limit}`);
  if (res.success) {
    if (process.env.NODE_ENV !== 'production') {
      try { console.debug('[LibraryAPI] Raw library payload:', res.data); } catch { }
    }
    const normalized = normalizeLibraryResponse(res.data);
    return {
      ...normalized,
      pagination: res.data?.pagination
    };
  }
  throw new Error(res.error || 'Failed to fetch user library');
}


export async function getUserLibraryByStatus(status: LibraryStatus, params: GetUserLibraryParams = {}): Promise<LibraryPaginatedResponse> {
  // Backend currently returns all; filter client-side for stability
  const full = await getUserLibrary(params);
  const items = (full.items || []).filter((i) => {
    if (status === 'saved') {
      return i.is_saved === true; // strictly server flag
    }
    if (status === 'completed') {
      return i.status === 'completed';
    }
    // strictly only items with status === 'in-progress'
    return i.status === 'in-progress';
  });
  return { items, count: items.length, pagination: full.pagination };
}

export async function saveBook(book_id: number): Promise<{ message: string; is_saved: boolean }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res = await bookServiceApi.post<any>('/library/save', { book_id });
  if (!res.success) {
    throw new Error(res.detail || res.error || 'Failed to toggle save book');
  }
  return { 
    message: res.message || (res.data?.is_saved ? 'کتاب ذخیره شد' : 'کتاب از ذخیره‌ها حذف شد'),
    is_saved: res.data?.is_saved ?? res.is_saved ?? true
  };
}

// Re-export purchaseBook from books.ts for convenience
export { purchaseBookApi as purchaseBook, type PurchaseBookResponse };
